// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { useLocalization, type Language } from '../../config/localization';
import Tooltip from '../ui/Tooltip';
import { getWidgetCollapsedState, setWidgetCollapsedState } from '../../utils/localStorage';

interface BaseWidgetProps {
  title: string;
  icon: string;
  description?: string;
  children: React.ReactNode;
  language: Language;
  className?: string;
  defaultCollapsed?: boolean;
  widgetId?: string; // Unique identifier for persisting collapsed state
}

export default function BaseWidget({
  title,
  icon,
  description,
  children,
  language,
  className = '',
  defaultCollapsed = false,
  widgetId
}: BaseWidgetProps) {
  const t = useLocalization(language);
  
  // Initialize collapsed state from localStorage if widgetId is provided
  const [isCollapsed, setIsCollapsed] = useState(() => {
    if (widgetId) {
      return getWidgetCollapsedState(widgetId, 'small', defaultCollapsed);
    }
    return defaultCollapsed;
  });

  // Save collapsed state to localStorage when it changes
  useEffect(() => {
    if (widgetId) {
      setWidgetCollapsedState(widgetId, 'small', isCollapsed);
    }
  }, [widgetId, isCollapsed]);

  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  return (
    <div className={`bg-gray-900/50 rounded-xl border border-gray-800/80 p-3 transition-all duration-200 ${className}`} style={{ position: 'relative', zIndex: 10, overflow: 'visible' }}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <span className="text-base">{icon}</span>
          <Tooltip content={description}>
            <h3 className="text-xs font-semibold text-white truncate cursor-help min-w-0 flex-1">{title}</h3>
          </Tooltip>
        </div>
        <button
          onClick={toggleCollapse}
          className="w-5 h-5 flex items-center justify-center rounded bg-gray-800/50 hover:bg-gray-700/50 text-gray-400 hover:text-white transition-all duration-200 hover:scale-105 flex-shrink-0 ml-2"
          title={isCollapsed ? (t.widgets?.expand || 'Expand') : (t.widgets?.collapse || 'Collapse')}
        >
          <span className="text-xs font-bold">
            {isCollapsed ? '+' : '−'}
          </span>
        </button>
      </div>
      <div className={`transition-all duration-300 ease-in-out overflow-hidden ${
        isCollapsed ? 'max-h-0 opacity-0' : 'max-h-[1000px] opacity-100'
      }`}>
        {children}
      </div>
    </div>
  );
}
